import { AttendanceReleaseControl } from '../attendance-management/attendance.types';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Auth } from '@aws-amplify/auth';
import { AuthService } from '../auth/auth.service';
import { CommonUtil } from '../util/common-util';

interface AttendanceState {
  brochure?:{name:string;url:string}|null;brochureNotice?:string;
  releaseControl?: AttendanceReleaseControl;
  attendanceMode: 'once' | 'daily'; attendanceDate: string | null;
  dailyAttendance: Array<{date: string; actualStatus: string; plannedStatus: string; included: boolean; canAnswer: boolean}>;
  operationNotice: string;
  scheduleNeedsUpdate?: boolean;
  sessions: Array<{start:string;end:string;checkinStart:string}>;
  scheduleKind: string;
  eventId: number;
  title: string;
  phase: 'answer' | 'waiting' | 'checkin' | 'finished';
  answerDeadline: string | null;
  checkinStart: string | null;
  eventEnd: string | null;
  plannedStatus: 'unanswered' | 'attending' | 'absent';
  plannedAt: string | null;
  actualStatus: 'not_checked_in' | 'attended' | 'absent';
  actualAt: string | null;
  canAnswer: boolean;
  canCheckIn: boolean;
  recordRevision: number;
}

@Component({
  selector: 'app-attendance-participant',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatProgressSpinnerModule],
  templateUrl: './attendance-participant.component.html',
  styleUrl: './attendance-participant.component.css'
})
export class AttendanceParticipantComponent implements OnInit {
  surveyLink:any;
  async openSurvey(){if(this.surveyLink?.url)this.router.navigateByUrl(this.surveyLink.url,{state:{participantKey:this.surveyLink.participantKey}});}
  state?: AttendanceState;
  showSpinner = true;
  errorMessage = '';
  private eventId = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private commonUtil: CommonUtil,
  ) {}

  ngOnInit(): void {
    this.eventId = Number(this.route.snapshot.paramMap.get('id'));
    this.load();
    this.loadSurveyLink();
  }

  async loadSurveyLink(){try{const options:any=this.auth.createApiHeader(await Auth.currentSession());options.body={id:this.eventId,action:'participant_link'};const result=await this.commonUtil.apiPost('/myevent/survey',options);this.surveyLink=result.data;}catch{this.surveyLink=undefined;}}

  answer(status: 'attending' | 'absent', attendanceDate?: string): void {
    this.submit('/myevent/attendance/answer', { plannedStatus: status, attendanceDate });
  }

  checkIn(): void {
    this.submit('/myevent/attendance/checkin', {attendanceDate:this.state?.attendanceDate});
  }

  goToMyEvent(): void {
    this.router.navigate(['/myevent']);
  }

  plannedLabel(status?: string): string {
    const value = status ?? this.state?.dailyAttendance.find(d=>d.date===this.state?.attendanceDate)?.plannedStatus ?? this.state?.plannedStatus;
    if (value === 'attending') return '出席予定';
    if (value === 'absent') return '欠席予定';
    return '未回答';
  }

  get todayExcluded(): boolean { return !!this.state?.dailyAttendance.some(day => day.date === this.state?.attendanceDate && !day.included); }

  formatDate(value: string | null): string {
    if (!value) return '';
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    }).format(new Date(value));
  }

  formatSession(start: string, end: string): string {
    const endLabel = start.slice(0,10) === end.slice(0,10)
      ? new Intl.DateTimeFormat('ja-JP', {hour:'2-digit',minute:'2-digit'}).format(new Date(end))
      : this.formatDate(end);
    return this.formatDate(start) + ' ～ ' + endLabel;
  }

  dayLabel(day: string): string {
    return new Intl.DateTimeFormat('ja-JP', {month:'long',day:'numeric',weekday:'short',timeZone:'Asia/Tokyo'}).format(new Date(day+'T00:00:00+09:00'));
  }
  actualLabel(status: string): string { return status === 'attended' ? '出席' : status === 'absent' ? '欠席' : '未登録'; }

  load(): void {
    this.showSpinner = true;
    this.errorMessage = '';
    Auth.currentSession().then(session => {
      const options: any = this.auth.createApiHeader(session);
      options.body = { id: this.eventId };
      return this.commonUtil.apiPost('/myevent/attendance/participant', options);
    }).then(res => {
      this.state = res.data;
    }).catch(err => {
      this.errorMessage = err?.error?.message || err?.message || '出欠情報を取得できませんでした。';
    }).finally(() => this.showSpinner = false);
  }

  private submit(path: string, extra: object): void {
    if (!this.state || this.showSpinner) return;
    if (this.state.releaseControl?.canWrite !== true) {
      this.errorMessage = this.state.releaseControl?.message || '出欠機能は現在、閲覧のみ利用できます。';
      return;
    }
    this.showSpinner = true;
    this.errorMessage = '';
    const body = {
      id: this.eventId,
      recordRevision: this.state.recordRevision,
      idempotencyKey: this.idempotencyKey(),
      ...extra,
    };
    Auth.currentSession().then(session => {
      const options: any = this.auth.createApiHeader(session);
      options.body = body;
      return this.commonUtil.apiPost(path, options);
    }).then(res => {
      this.state = res.data;
    }).catch(err => {
      this.errorMessage = err?.error?.message || err?.message || '登録できませんでした。再読み込みしてください。';
    }).finally(() => this.showSpinner = false);
  }

  private idempotencyKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
